import { useEffect, useState } from "react";

/**
 * Drives mount/visible state for enter + exit transitions on a conditionally
 * rendered element (overlays, sheets). Returns `render` — whether to mount the
 * DOM — and `show` — whether to apply the "visible" classes.
 *
 * - open → true:  render immediately, then (next frame) show = true → enter.
 * - open → false: show = false → exit; after `duration` ms, render = false.
 *
 * The two-frame delay before `show` lets the browser paint the hidden state
 * first, so the CSS transition actually runs; the unmount is timed to
 * `duration` rather than `transitionend` (which is flaky across browsers).
 */
export function useEnterExit(open: boolean, duration = 200): { render: boolean; show: boolean } {
  const [render, setRender] = useState(open);
  const [show, setShow] = useState(open);

  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    if (open) {
      setRender(true);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShow(true));
      });
    } else {
      setShow(false);
      timer = window.setTimeout(() => setRender(false), duration);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
    };
  }, [open, duration]);

  return { render, show };
}
